"use client";

import {
  Button,
  Flex,
  Icon,
  Image,
  Modal,
  ScrollPane,
  Text,
} from "@huub-web/ui";
import { type FC, type Ref, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  contentWrapperStyle,
  footerStyle,
  modalConstraintStyle,
  photoGridStyle,
  modalTitleStyle,
  photoItemStyle,
  radioIndicatorStyle,
  scrollableContentStyle,
} from "./PhotoSelectionModal.css";

export type AnalysisPhoto = {
  /**
   * UUID from property_photos table
   */
  id: string;
  /**
   * Original Funda CDN URL
   */
  sourceUrl: string | null;
  /**
   * Room classification (kitchen, bedroom, etc.)
   */
  roomType: string | null;
  /**
   * Sequential Funda photo index
   */
  photoNumber: number | null;
};

export type PhotoSelectionModalProps = {
  /**
   * Photos to display
   */
  photos: AnalysisPhoto[];
  /**
   * Whether photos are currently loading
   */
  loading: boolean;
  /**
   * Error message if photo fetch failed, null otherwise
   */
  error: string | null;
  /**
   * Currently selected photo ID
   */
  selectedPhotoId: string | null;
  /**
   * Property address displayed as subtitle
   */
  address?: string;
  /**
   * Called when the user selects a photo
   */
  onSelect: ({ photoId }: { photoId: string }) => void;
  /**
   * Called when the user closes the modal
   */
  onClose: () => void;
  /**
   * Called when the user confirms their photo selection
   */
  onConfirm: ({ photoId }: { photoId: string }) => void;
  /**
   * Called when the user clicks retry after an error
   */
  onRetry: () => void;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * Extracted to avoid placing biome-ignore inside .map() return expressions.
 */
type PhotoItemProps = {
  photo: AnalysisPhoto & { sourceUrl: string };
  index: number;
  isSelected: boolean;
  isFocused: boolean;
  onSelect: PhotoSelectionModalProps["onSelect"];
};

const PhotoItem: FC<PhotoItemProps> = ({
  photo,
  index,
  isSelected,
  isFocused,
  onSelect,
}) => {
  const { t } = useTranslation();
  return (
    // biome-ignore lint/a11y/useSemanticElements: custom image-radio needs a div wrapper for CSS grid layout; native <input type="radio"> cannot host image children
    <div
      className={photoItemStyle}
      role="radio"
      aria-checked={isSelected}
      tabIndex={isFocused ? 0 : -1}
      onClick={() => onSelect({ photoId: photo.id })}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onSelect({ photoId: photo.id });
        }
      }}
    >
      <Image
        src={photo.sourceUrl}
        alt={
          photo.roomType ??
          t("photoSelection.photoAlt", {
            number: photo.photoNumber ?? index + 1,
          })
        }
        width="100%"
        height="100%"
        borderRadius
        forceHover={isSelected}
        lazy={index > 0}
      >
        <div className={radioIndicatorStyle({ selected: isSelected })} />
      </Image>
    </div>
  );
};

/**
 * Photo selection modal for choosing a property photo to renovate
 * in the Interior Designer. Pure presentational — all state is
 * controlled by the parent.
 */
export const PhotoSelectionModal: FC<PhotoSelectionModalProps> = ({
  photos,
  loading,
  error,
  selectedPhotoId,
  address,
  onSelect,
  onClose,
  onConfirm,
  onRetry,
  ref,
}) => {
  const { t } = useTranslation();
  const gridRef = useRef<HTMLDivElement>(null);
  const renderablePhotos = photos.filter(
    (p): p is AnalysisPhoto & { sourceUrl: string } => p.sourceUrl !== null,
  );

  const modalTitle = address ? (
    <Flex as="span" direction="column" gap="xxs" className={modalTitleStyle}>
      <span>{t("photoSelection.title")}</span>
      <Text as="span" variant="bodyS" color="placeholder">
        {address}
      </Text>
    </Flex>
  ) : (
    t("photoSelection.title")
  );

  const handleGridKeyDown = (e: React.KeyboardEvent) => {
    if (!gridRef.current || renderablePhotos.length === 0) return;
    const items =
      gridRef.current.querySelectorAll<HTMLElement>('[role="radio"]');
    const currentIndex = Array.from(items).indexOf(
      document.activeElement as HTMLElement,
    );
    if (currentIndex === -1) return;

    const columns = window
      .getComputedStyle(gridRef.current)
      .gridTemplateColumns.split(" ").length;
    let nextIndex = currentIndex;

    switch (e.key) {
      case "ArrowRight":
        nextIndex = (currentIndex + 1) % renderablePhotos.length;
        break;
      case "ArrowLeft":
        nextIndex =
          (currentIndex - 1 + renderablePhotos.length) %
          renderablePhotos.length;
        break;
      case "ArrowDown":
        nextIndex = Math.min(
          currentIndex + columns,
          renderablePhotos.length - 1,
        );
        break;
      case "ArrowUp":
        nextIndex = Math.max(currentIndex - columns, 0);
        break;
      default:
        return;
    }

    e.preventDefault();
    items[nextIndex]?.focus();
    onSelect({ photoId: renderablePhotos[nextIndex].id });
  };

  let content: React.ReactNode;

  if (loading) {
    content = (
      <div className={contentWrapperStyle}>
        <Flex align="center" justify="center" gap="xs" padding="xl">
          <Icon icon="loading" animation="rotation" size="m" />
          <Text>{t("photoSelection.loading")}</Text>
        </Flex>
      </div>
    );
  } else if (error) {
    content = (
      <div className={contentWrapperStyle}>
        <Flex direction="column" align="center" gap="m" padding="xl">
          <Text>{t("photoSelection.error")}</Text>
          <Button variant="opaque" border="default" onClick={onRetry}>
            {t("photoSelection.retry")}
          </Button>
        </Flex>
      </div>
    );
  } else if (renderablePhotos.length === 0) {
    content = (
      <div className={contentWrapperStyle}>
        <Flex align="center" justify="center" padding="xl">
          <Text>{t("photoSelection.empty")}</Text>
        </Flex>
      </div>
    );
  } else {
    const focusedIndex = renderablePhotos.findIndex(
      (p) => p.id === selectedPhotoId,
    );

    content = (
      <>
        <ScrollPane
          className={scrollableContentStyle}
          mode="vertical"
          overlapColor="background"
          hideScrollbar
        >
          <div
            ref={gridRef}
            className={photoGridStyle}
            role="radiogroup"
            aria-label={t("photoSelection.title")}
            onKeyDown={handleGridKeyDown}
          >
            {renderablePhotos.map((photo, index) => {
              const isSelected = selectedPhotoId === photo.id;
              const isFocused =
                focusedIndex === -1 ? index === 0 : index === focusedIndex;
              return (
                <PhotoItem
                  key={photo.id}
                  photo={photo}
                  index={index}
                  isSelected={isSelected}
                  isFocused={isFocused}
                  onSelect={onSelect}
                />
              );
            })}
          </div>
        </ScrollPane>

        <div className={footerStyle}>
          <Button
            onClick={() => {
              if (selectedPhotoId) {
                onConfirm({ photoId: selectedPhotoId });
              }
            }}
            disabled={!selectedPhotoId}
            trailing={<Icon icon="chevronRight" />}
          >
            {t("photoSelection.confirm")}
          </Button>
        </div>
      </>
    );
  }

  return (
    <Modal
      ref={ref}
      size="xl"
      closeable
      escape
      className={modalConstraintStyle}
      title={modalTitle}
      onClose={onClose}
    >
      {content}
    </Modal>
  );
};
